/**
 * Criterion Mapping (v2 / redesign) — Phase 3 of the 4-phase OBS appointment.
 *
 * Rendered only for patients whose stamped workflow config is v2
 * (obsFlow.version === 'v2'). v1 patients get 3_CriterionMappingLegacy.js
 * instead; 3_CriterionMapping.js is the thin switch between the two.
 *
 * Redesign (criteria-by-criteria)
 * -------------------------------
 * Clinicians work through one DSM / behaviour section at a time (A1–B4 plus
 * Self-Reported Behaviours and Other Behaviours), reviewing notes that were
 * tagged upstream in Task Administration and ADOS-informed Coding. For each
 * DSM criterion they record a single evidence decision. Catch-all behaviour
 * pages show tagged notes only (no decision) — they feed the LLM narrative.
 *
 * This replaces the previous task-by-task drag-and-drop mapping table, which
 * clinicians found too tedious.
 *
 * LLM generation (SSI parity):
 *   • Criteria paragraphs are generated when the clinician LEAVES this phase
 *     through any managed exit (back to ADOS, forward to Review) or through an
 *     unmanaged one (breadcrumb / browser back, via the exit-regeneration hook).
 *     Switching between criteria inside the phase does NOT generate.
 *   • Partial mapping still generates, provided at least one evidence decision
 *     exists; only a completely undecided phase is skipped.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { ExclamationIcon } from '@heroicons/react/solid';
import Layout from '../../../../components/Layout';
import Breadcrumb from '../../../../components/Breadcrumb';
import AppointmentChecklistControls from '../../../../components/autismModular/AppointmentChecklistControls';
import PreviewModeBanner from '../../../../components/PreviewModeBanner';
import ModularSideNavbar from '../../../../components/autismModular/ModularSideNavbar';
import ModularStepFooter from '../../../../components/autismModular/ModularStepFooter';
import ObsCriterionMappingPanel from '../components/ObsCriterionMappingPanel';
import { useDragAutoScroll } from '../../../../hooks/useDragAutoScroll';
import { useModularSectionExitRegeneration } from '../../../../hooks/useModularSectionExitRegeneration';
import { TriggerGenerateDocumentSection } from '../../../../utils/TriggerGenerateDocumentSection';
import { getAutismOBSStepRoutes } from '../../../../utils/autismObsRouting';
import {
  hasAnyObservationCriterionDecision,
  listIncompleteObservationCriteria,
  areAllCriteriaEvidenceComplete,
} from '../../../../utils/observationCriteriaCompletion';
import {
  shouldGenerateSelfReportSection,
  hasSelfReportNoteContent,
  selfReportedTaggingChanged,
  criteriaRelevantNotesChanged,
  SELF_REPORT_DATA_KEY,
  SELF_REPORTED_TAG_ID,
} from '../../../../utils/obsSelfReportGeneration';
import {
  OBS_CRITERION_MAPPING_PAGES,
  OBS_CRITERION_MAPPING_DSM_PAGES,
  OBS_CRITERION_MAPPING_BEHAVIOUR_PAGES,
} from '../config/obsNoteTagOptions';
import { collectObsTaggedNotes } from '../utils/collectObsTaggedNotes';
import useObservationStageData from '../hooks/useObservationStageData';

export default function CriterionMappingV2() {
  const {
    patientId,
    tasks,
    navigate,
    flushStageWrites,
    isPreviewMode,
    canEdit,
    reportContent,
    selectedTaskIds,
    allTaskData,
    adosData,
    criteriaEvidence,
    criteriaNotes,
    handleCriteriaEvidenceChange,
    handleCriteriaNotesChange,
    handleGeneralFieldChange,
    handleUpdateNoteCriteriaTags,
    isAppointmentChecklistEnabled,
  } = useObservationStageData();

  const showChecklist = isAppointmentChecklistEnabled;

  const [hasChanges, setHasChanges] = useState(false);
  // Self-Report paragraph dirties separately — re-tags / Self-Report box edits
  // made here must refresh that section even when Criteria itself is unchanged.
  const [selfReportDirty, setSelfReportDirty] = useState(false);
  const selfReportHandledRef = useRef(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [activePageId, setActivePageId] = useState(
    () => OBS_CRITERION_MAPPING_PAGES[0]?.id || 'A1',
  );

  const canGenerateCriteria = useMemo(
    () => hasAnyObservationCriterionDecision(
      selectedTaskIds, tasks, allTaskData, criteriaEvidence,
    ),
    [selectedTaskIds, tasks, allTaskData, criteriaEvidence],
  );

  const incompleteCriteria = useMemo(
    () => listIncompleteObservationCriteria(criteriaEvidence),
    [criteriaEvidence],
  );

  const { markRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: 'observation',
    sectionTitle: 'Criteria',
    hasChanges,
    canEdit,
    reportContent,
    shouldRegenerate: canGenerateCriteria,
    // Initial generation of the Criteria section is owned exclusively by the
    // forward step into Review (below) plus the Review-page catch-up net. An
    // unmanaged/side exit from here (breadcrumb, browser back, or the back
    // button to ADOS) must therefore only REGENERATE an already-drafted
    // section — never draft one for the first time.
    suppressInitialGeneration: true,
  });

  /**
   * Flush pending writes and (maybe) (re)generate report sections on exit.
   *
   * Criteria — the single most important rule of the v2 OBS pipeline lives here:
   * initial generation is a FORWARD-only action. Proceeding forward into Review
   * is the one moment we are willing to draft the Criteria paragraphs for the
   * first time; every other exit may only refresh paragraphs that already exist.
   *
   * Self-Report — first draft is owned by the forward step into Review
   * (alongside Criteria / Other Behaviours). Every other exit from this phase
   * may only refresh Self-Report when it was already generated and dirtied.
   *
   * @param {boolean} isForwardToReview - true when the clinician is stepping
   *   forward into Review & Synthesis (initial Criteria + Self-Report draft
   *   allowed); false for a backward/side managed exit such as "Back to ADOS".
   */
  const flushAndGenerateOnExit = useCallback(async (isForwardToReview) => {
    markRegenHandled();
    const dataReady = flushStageWrites('observation');
    const stageReport = reportContent?.observation;
    const generated = stageReport?.stageSectionsGenerated || [];

    if (canEdit && canGenerateCriteria) {
      const isGenerated = generated.includes('Criteria');
      // Forward into Review: draft if there are edits OR it was never generated.
      // Backward/side exit: only refresh an already-generated section when dirty.
      const shouldFire = isForwardToReview
        ? (hasChanges || !isGenerated)
        : (isGenerated && hasChanges);
      if (shouldFire) {
        TriggerGenerateDocumentSection(
          patientId, 'Autism Behavioural Observation', 'Criteria', [], dataReady,
        );
      }
    }

    if (canEdit && shouldGenerateSelfReportSection({
      hasContent: hasSelfReportNoteContent(allTaskData, adosData),
      hasChanges: selfReportDirty,
      isGenerated: generated.includes('Self-Report'),
      // First Self-Report draft rides with Criteria on the Review forward only.
      allowInitialGeneration: isForwardToReview,
    })) {
      selfReportHandledRef.current = true;
      TriggerGenerateDocumentSection(
        patientId, 'Autism Behavioural Observation', 'Self-Report', [], dataReady,
      );
    }

    await dataReady;
  }, [
    markRegenHandled,
    flushStageWrites,
    canEdit,
    canGenerateCriteria,
    reportContent,
    hasChanges,
    selfReportDirty,
    allTaskData,
    adosData,
    patientId,
  ]);

  const contentRef = useRef(null);
  useDragAutoScroll(contentRef);

  const activePage = useMemo(
    () => OBS_CRITERION_MAPPING_PAGES.find((p) => p.id === activePageId)
      || OBS_CRITERION_MAPPING_PAGES[0],
    [activePageId],
  );

  const { taskNotes, adosNotes } = useMemo(() => {
    const tagged = collectObsTaggedNotes({
      tagId: activePage?.id,
      selectedTaskIds,
      tasks,
      allTaskData,
      adosData,
    });
    // Self-Reported page: notes typed in the Task Admin Self-Report box are
    // shown in the third notes area (not duplicated in the Task Admin column).
    if (activePage?.id === SELF_REPORTED_TAG_ID) {
      return {
        taskNotes: tagged.taskNotes.filter(
          (n) => n?.sourceRef?.dataKey !== SELF_REPORT_DATA_KEY,
        ),
        adosNotes: tagged.adosNotes,
      };
    }
    return tagged;
  }, [activePage?.id, selectedTaskIds, tasks, allTaskData, adosData]);

  // Notes typed directly into Task Admin Self-Report — third box on the SR page.
  const selfReportPanelNotes = useMemo(() => {
    const notes = allTaskData?.[SELF_REPORT_DATA_KEY]?.notes;
    return Array.isArray(notes) ? notes : [];
  }, [allTaskData]);

  // One pass over all notes so sidebar badges can show "started" / "completed"
  // without re-scanning per page on every render.
  const taggedNoteCountsByPage = useMemo(() => {
    const counts = {};
    for (const page of OBS_CRITERION_MAPPING_PAGES) {
      const tagged = collectObsTaggedNotes({
        tagId: page.id,
        selectedTaskIds,
        tasks,
        allTaskData,
        adosData,
      });
      counts[page.id] = tagged.taskNotes.length + tagged.adosNotes.length;
    }
    // Self-Reported sidebar also counts untagged notes still sitting in the
    // Self-Report box (legacy / pre-auto-tag), so the tick stays honest.
    const panelNotes = allTaskData?.[SELF_REPORT_DATA_KEY]?.notes;
    if (Array.isArray(panelNotes)) {
      const untaggedDirect = panelNotes.filter((n) => (
        n
        && String(n.text || '').trim()
        && !(Array.isArray(n.criteriaTags) && n.criteriaTags.includes(SELF_REPORTED_TAG_ID))
      )).length;
      counts[SELF_REPORTED_TAG_ID] = (counts[SELF_REPORTED_TAG_ID] || 0) + untaggedDirect;
    }
    return counts;
  }, [selectedTaskIds, tasks, allTaskData, adosData]);

  const pageStatus = useCallback((page) => {
    const evidence = criteriaEvidence?.[page.id];
    const noteCount = taggedNoteCountsByPage[page.id] || 0;
    if (page.hasEvidenceDecision) {
      if (evidence) return 'completed';
      if (noteCount > 0) return 'started';
      return 'default';
    }
    // Catch-all pages have no evidence decision — treat any notes as "done".
    return noteCount > 0 ? 'completed' : 'default';
  }, [criteriaEvidence, taggedNoteCountsByPage]);

  // DSM criteria above the divider; Self-Reported / Other below it (extraRows).
  const sidebarItems = useMemo(
    () => OBS_CRITERION_MAPPING_DSM_PAGES.map((page) => ({
      id: page.id,
      badge: page.badge,
      label: page.shortTitle,
      fullTitle: page.fullTitle,
      title: page.shortTitle,
      status: pageStatus(page),
    })),
    [pageStatus],
  );

  const sidebarExtraRows = useMemo(
    () => OBS_CRITERION_MAPPING_BEHAVIOUR_PAGES.map((page) => ({
      id: page.id,
      badge: page.badge,
      label: page.shortTitle,
      fullTitle: page.fullTitle,
      title: page.shortTitle,
      status: pageStatus(page),
      active: activePageId === page.id,
      onClick: () => setActivePageId(page.id),
    })),
    [pageStatus, activePageId],
  );

  const pageIndex = useMemo(
    () => OBS_CRITERION_MAPPING_PAGES.findIndex((p) => p.id === activePageId),
    [activePageId],
  );
  const prevPage = pageIndex > 0 ? OBS_CRITERION_MAPPING_PAGES[pageIndex - 1] : null;
  const nextPage = pageIndex >= 0 && pageIndex < OBS_CRITERION_MAPPING_PAGES.length - 1
    ? OBS_CRITERION_MAPPING_PAGES[pageIndex + 1]
    : null;

  const handleEvidenceChange = useCallback((value) => {
    if (!activePage?.hasEvidenceDecision) return;
    handleCriteriaEvidenceChange(activePage.id, value);
    setHasChanges(true);
  }, [activePage, handleCriteriaEvidenceChange]);

  const handleNotesChange = useCallback((notes) => {
    if (!activePage?.id) return;
    handleCriteriaNotesChange(activePage.id, notes);
    setHasChanges(true);
  }, [activePage, handleCriteriaNotesChange]);

  // Self-Reported third box writes back to the Task Admin Self-Report array.
  // Keep Criteria dirty (`hasChanges`) separate from Self-Report dirty so a
  // plain Self-Report edit does not regenerate all seven criteria paragraphs.
  const handleSelfReportNotesChange = useCallback((notes) => {
    const prevNotes = allTaskData?.[SELF_REPORT_DATA_KEY]?.notes;
    handleGeneralFieldChange('self_reported', 'notes', notes);
    setSelfReportDirty(true);
    // Dual-tagged notes in the Self-Report box (e.g. A1 + self_reported) also
    // feed Criteria — only then mark Criteria dirty.
    if (criteriaRelevantNotesChanged(prevNotes, notes)) {
      setHasChanges(true);
    }
  }, [handleGeneralFieldChange, allTaskData]);

  // Re-tag from Criterion Mapping — same storage as Task Admin / ADOS, so
  // navigating back to those phases shows the updated criteriaTags.
  const handleRetagNote = useCallback((sourceRef, noteId, criteriaTags) => {
    // Look up the live note so we can dirty Self-Report / Criteria independently
    // (self_reported-only vs DSM/`other` tag membership).
    let prevNotes = [];
    if (sourceRef?.kind === 'ados') {
      const field = sourceRef.field || `ados_${sourceRef.dataKey}_notes`;
      prevNotes = Array.isArray(adosData?.[field]) ? adosData[field] : [];
    } else if (sourceRef?.kind === 'task') {
      const entry = allTaskData?.[sourceRef.dataKey]
        || allTaskData?.[Number(sourceRef.dataKey)]
        || {};
      prevNotes = Array.isArray(entry?.[sourceRef.field]) ? entry[sourceRef.field] : [];
    }
    const nextNotes = prevNotes.map((n) => (
      n && n.id === noteId ? { ...n, criteriaTags } : n
    ));
    if (selfReportedTaggingChanged(prevNotes, nextNotes)) {
      setSelfReportDirty(true);
    }
    if (criteriaRelevantNotesChanged(prevNotes, nextNotes)) {
      setHasChanges(true);
    }

    handleUpdateNoteCriteriaTags(sourceRef, noteId, criteriaTags);
  }, [handleUpdateNoteCriteriaTags, adosData, allTaskData]);

  // Unmanaged-exit net for Self-Report (breadcrumb, browser back). Criteria is
  // already covered by useModularSectionExitRegeneration; Self-Report rides a
  // plain unmount net so a re-tag that never hit a managed CTA still refreshes.
  const selfReportExitRef = useRef({
    selfReportDirty: false,
    allTaskData: {},
    adosData: {},
    reportContent: null,
    canEdit: false,
    patientId: null,
  });
  selfReportExitRef.current = {
    selfReportDirty,
    allTaskData,
    adosData,
    reportContent,
    canEdit,
    patientId,
  };
  useEffect(() => () => {
    if (selfReportHandledRef.current) return;
    const snap = selfReportExitRef.current;
    if (!snap.canEdit) return;
    // Unmanaged exit (breadcrumb / browser back): regenerate only — never a
    // first Self-Report draft outside the Review forward path.
    if (!shouldGenerateSelfReportSection({
      hasContent: hasSelfReportNoteContent(snap.allTaskData, snap.adosData),
      hasChanges: snap.selfReportDirty,
      isGenerated: Boolean(
        snap.reportContent?.observation?.stageSectionsGenerated?.includes('Self-Report'),
      ),
      allowInitialGeneration: false,
    })) return;
    TriggerGenerateDocumentSection(
      snap.patientId, 'Autism Behavioural Observation', 'Self-Report', [],
    );
  }, []);

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Behavioural Observation'],
    ['Criterion Mapping'],
    ...(activePage ? [[activePage.fullTitle]] : []),
  ];

  const handleBackToAdos = useCallback(async () => {
    // Backward managed exit — Criteria / Self-Report regeneration only (never
    // a first draft for either section).
    await flushAndGenerateOnExit(false);
    navigate(getAutismOBSStepRoutes(patientId).adosCoding);
  }, [flushAndGenerateOnExit, navigate, patientId]);

  const navigateToReview = useCallback(async () => {
    // Forward managed exit into Review — the one place initial Criteria and
    // Self-Report drafts fire together (Other Behaviours rides with Criteria).
    await flushAndGenerateOnExit(true);
    navigate(getAutismOBSStepRoutes(patientId).review);
  }, [flushAndGenerateOnExit, navigate, patientId]);

  /**
   * Soft gate to Review — mirrors Adult SSI:
   *   • Preview / fully decided → go straight through
   *   • Incomplete A1–B4 decisions → warning dialog (Proceed Anyway still allowed)
   */
  const handleProceedToReview = useCallback(() => {
    if (!isPreviewMode && !areAllCriteriaEvidenceComplete(criteriaEvidence)) {
      setShowValidationDialog(true);
      return;
    }
    navigateToReview();
  }, [isPreviewMode, criteriaEvidence, navigateToReview]);

  const proceedToReviewAnyway = useCallback(() => {
    setShowValidationDialog(false);
    navigateToReview();
  }, [navigateToReview]);

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0">
        <Breadcrumb
          tupleList={breadcrumbTuples}
          allowWrap={showChecklist}
          rightSlot={showChecklist
            ? <AppointmentChecklistControls appointmentType="observation" />
            : undefined
          }
        />
      </div>

      <div className="flex flex-1 min-h-0">
        <ModularSideNavbar
          header={{ title: 'Criterion Mapping', subtitle: 'DSM-5 criteria' }}
          items={sidebarItems}
          activeId={activePageId}
          onSelect={setActivePageId}
          mode="expanded"
          // Self-Reported + Other Behaviours sit below a divider, mirroring
          // Task Administration's Tasks → Self-Report split.
          extraRows={sidebarExtraRows}
        />

        <div className="flex-1 min-w-0 flex flex-col">
          {isPreviewMode && <PreviewModeBanner />}
          <div ref={contentRef} className="flex-1 min-h-0 overflow-y-auto">
            <ObsCriterionMappingPanel
              page={activePage}
              evidence={criteriaEvidence?.[activePage?.id] ?? null}
              onEvidenceChange={handleEvidenceChange}
              taskNotes={taskNotes}
              adosNotes={adosNotes}
              criterionNotes={
                activePage?.id === SELF_REPORTED_TAG_ID
                  ? []
                  : (criteriaNotes?.[activePage?.id] || [])
              }
              onCriterionNotesChange={handleNotesChange}
              selfReportNotes={
                activePage?.id === SELF_REPORTED_TAG_ID ? selfReportPanelNotes : null
              }
              onSelfReportNotesChange={
                activePage?.id === SELF_REPORTED_TAG_ID
                  ? handleSelfReportNotesChange
                  : null
              }
              onUpdateNoteTags={handleRetagNote}
              readOnly={!canEdit}
            />
          </div>

          <ModularStepFooter
            showBack={!!prevPage}
            backLabel={prevPage?.shortTitle}
            onBack={prevPage ? () => setActivePageId(prevPage.id) : undefined}
            onModuleExit={!prevPage ? handleBackToAdos : undefined}
            moduleExitLabel={!prevPage ? 'Back to ADOS-Informed Coding' : undefined}
            phaseBackLink={
              prevPage
                ? { label: 'Back to ADOS-Informed Coding', onClick: handleBackToAdos }
                : undefined
            }
            counterText={`${pageIndex + 1} of ${OBS_CRITERION_MAPPING_PAGES.length}`}
            nextLabel={nextPage ? nextPage.shortTitle : 'Go to Review and Synthesis'}
            onNext={nextPage
              ? () => setActivePageId(nextPage.id)
              : handleProceedToReview}
            phaseForwardLink={
              nextPage
                ? {
                    label: 'Go to Review and Synthesis',
                    onClick: handleProceedToReview,
                  }
                : undefined
            }
          />
        </div>
      </div>

      {showValidationDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowValidationDialog(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[560px] max-w-[90vw] text-left">
            <button
              type="button"
              onClick={() => setShowValidationDialog(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none"
              aria-label="Close"
            >
              &times;
            </button>
            <div className="flex items-center gap-2 mb-4">
              <ExclamationIcon className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <h2 className="text-lg font-bold text-gray-900">
                Evidence Decisions Required
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              The following DSM criteria are missing an evidence decision.
            </p>
            <ul className="bg-red-50 rounded-lg p-3 pl-5 mb-4 list-disc list-inside space-y-1">
              {incompleteCriteria.map((c) => (
                <li key={c.criterionId} className="text-sm text-red-800 font-medium">
                  {c.criterionId}. {c.shortTitle}
                </li>
              ))}
            </ul>
            <p className="text-sm italic text-amber-700 bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-5">
              <strong>Note:</strong> If you proceed without completing these, the
              criteria summary may be incomplete.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  const firstMissing = incompleteCriteria[0];
                  if (firstMissing) {
                    setActivePageId(firstMissing.criterionId);
                    setShowValidationDialog(false);
                  }
                }}
                className="flex items-center py-2 px-4 rounded-md shadow-md bg-primary text-white hover:bg-primary/90 transition-all duration-200 font-semibold text-sm"
              >
                Go back to DSM Criteria
              </button>
              <button
                type="button"
                onClick={proceedToReviewAnyway}
                className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 hover:bg-gray-50 transition-all duration-200 font-semibold text-sm"
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
}
